import React from 'react';

import styled from 'styled-components';

import ChevronRight from 'common/Icons/ChevronRight';
import { StyledTable } from 'common/Table/StyledTable';
import GradientBorderButton from 'components/lib/GradientBorderButton';
import { AICompetitiveIntelligenceRankingRow } from 'types/recommendations';

import AiCiTableWrapper, {
  AiCiSection,
  AiCiSectionTitle,
  CommunityName,
  CommunitySubtitle,
  RankCell,
} from './AiCiTableWrapper';
import ScorePill from './ScorePill';

const COMMUNITY_COLUMN_WIDTH = '30%';
const SCORE_COLUMN_WIDTH = '22%';
const DESCRIPTION_COLUMN_WIDTH = '48%';
const RANK_COLUMN_WIDTH = 60;
const DETAILS_COLUMN_WIDTH = 170;
const MIN_TABLE_WIDTH_WITH_DETAILS = 800;
const DETAILS_COLUMN_KEY = 'details-action';

type Props = {
  rows: AICompetitiveIntelligenceRankingRow[];
  primaryCommunityName?: string | null;
  showPrimaryDetailsButton?: boolean;
  onPrimaryDetailsClick?: () => void;
  title?: string;
  bare?: boolean;
};

const DetailsText = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const DetailsButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const ScoreCell = styled.div`
  /* Keep the score and comparison readable when the responsive column becomes narrow. */
  .row {
    flex-wrap: wrap;
  }
`;

const RankingsTable = ({
  rows,
  primaryCommunityName,
  showPrimaryDetailsButton = false,
  onPrimaryDetailsClick,
  title = 'Ranking Table',
  bare = false,
}: Props) => {
  const hasDetailsAction = showPrimaryDetailsButton && Boolean(onPrimaryDetailsClick);
  const isPrimary = (row: AICompetitiveIntelligenceRankingRow) =>
    primaryCommunityName != null && row.community === primaryCommunityName;

  const completeComparison = rows.every((row) => row.score != null);
  const competitorScores = rows
    .filter((r) => !isPrimary(r))
    .map((r) => r.score)
    .filter((score): score is number => score != null);
  const marketAverage =
    completeComparison && competitorScores.length
      ? Math.round(competitorScores.reduce((acc, s) => acc + s, 0) / competitorScores.length)
      : null;

  const columns = [
    {
      title: 'RANK',
      dataIndex: 'rank',
      key: 'rank',
      width: RANK_COLUMN_WIDTH,
      render: (rank: number | null, row: AICompetitiveIntelligenceRankingRow) => (
        <RankCell>{row.score != null ? (rank ?? '—') : '—'}</RankCell>
      ),
    },
    {
      title: '',
      dataIndex: 'community',
      key: 'community',
      width: COMMUNITY_COLUMN_WIDTH,
      render: (_: unknown, row: AICompetitiveIntelligenceRankingRow) => (
        <>
          <CommunityName>{row.community}</CommunityName>
          <CommunitySubtitle className={isPrimary(row) ? 'primary' : ''}>
            {isPrimary(row) ? 'My Community' : 'Competitor'}
          </CommunitySubtitle>
        </>
      ),
    },
    {
      title: 'SCORE',
      dataIndex: 'score',
      key: 'score',
      width: SCORE_COLUMN_WIDTH,
      render: (score: number | null) =>
        score == null ? (
          <DetailsText>—</DetailsText>
        ) : (
          <ScoreCell>
            <ScorePill score={score} average={marketAverage} isSmall />
          </ScoreCell>
        ),
    },
    {
      title: 'DESCRIPTION',
      dataIndex: 'explanation',
      key: 'explanation',
      width: DESCRIPTION_COLUMN_WIDTH,
      render: (explanation: string | null) => <DetailsText>{explanation}</DetailsText>,
    },
    {
      title: '',
      key: DETAILS_COLUMN_KEY,
      width: DETAILS_COLUMN_WIDTH,
      render: (_: unknown, row: AICompetitiveIntelligenceRankingRow) => {
        if (!isPrimary(row)) return null;

        return (
          <DetailsButtonWrapper>
            <GradientBorderButton
              size="small"
              onClick={onPrimaryDetailsClick}
              data-test-id="aici-ranking-scoring-rubric"
            >
              Scoring Rubric <ChevronRight fill="var(--competitor-purple)" width={16} height={16} />
            </GradientBorderButton>
          </DetailsButtonWrapper>
        );
      },
    },
  ];

  const table = (
    <AiCiTableWrapper>
      <StyledTable
        columns={(hasDetailsAction ? columns : columns.filter((column) => column.key !== DETAILS_COLUMN_KEY)) as any}
        dataSource={rows}
        rowKey={((row: AICompetitiveIntelligenceRankingRow) => `${row.rank}-${row.community}`) as any}
        rowClassName={((row: AICompetitiveIntelligenceRankingRow) => (isPrimary(row) ? 'primary' : '')) as any}
        tableLayout="fixed"
        scroll={hasDetailsAction ? { x: MIN_TABLE_WIDTH_WITH_DETAILS } : undefined}
        pagination={false}
        size="middle"
        bordered={false}
      />
    </AiCiTableWrapper>
  );

  if (bare) return table;

  return (
    <AiCiSection>
      <AiCiSectionTitle>{title}</AiCiSectionTitle>
      {table}
    </AiCiSection>
  );
};

export default RankingsTable;
